import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { BarChart3, Users, HelpCircle } from 'lucide-react'
import type { PageMeta } from '../../../lib/permissions/pageMeta'
import { PageHeader } from '../../../components/ui/PageHeader'
import { EmptyState } from '../../../components/ui/EmptyState'
import { Spinner } from '../../../components/ui/Spinner'
import { SearchInput } from '../../../components/ui/SearchInput'
import { Pagination } from '../../../components/ui/Pagination'
import { DateRangePicker } from '../../../components/ui/DateRangePicker'
import { ExportButton } from '../../../components/common/ExportButton'
import { useDateRange } from '../../../hooks/useDateRange'
import { useDebounce } from '../../../hooks/useDebounce'
import { useCurrency } from '../../../hooks/useCurrency'
import { useAuthStore } from '../../../store/authStore'
import { useStaff } from '../../../hooks/useStaff'
import { fetchSalesPerRep, fetchSalesPerRepExport, type SalesPerRepFilters } from '../../../lib/salesRepReport'

const PILL_TABS = ['today', 'week', 'month', 'year', 'custom'] as const

export const pageMeta: PageMeta = {
  key: 'trading.sales_per_rep',
  label: 'Sales per Rep',
  module: 'Trading',
  section: 'Reports',
  description: 'Sales attributed to each representative in the selected period',
  defaultPermissions: {
    owner: 'view', admin: 'view', manager: 'view',
    accountant: 'none', cashier: 'none', viewer: 'none',
  },
}

export default function SalesPerRepPage() {
  const { tenant } = useAuthStore()
  const tid = tenant?.id ?? ''
  const { format } = useCurrency()
  const dr = useDateRange('month')

  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 350)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)

  const filters = useMemo<SalesPerRepFilters>(
    () => ({ from: dr.from, to: dr.to, search: debouncedSearch.trim() }),
    [dr.from, dr.to, debouncedSearch],
  )
  useEffect(() => { setPage(1) }, [filters])

  const { data: staff = [] } = useStaff(tid)
  // A rep is non-staff ("external", mig 422) when its id is not a staff id —
  // reps are staff or external, so a non-staff id is an external rep.
  const staffIds = useMemo(() => new Set(staff.map(s => s.id)), [staff])
  const { data, isLoading } = useQuery({
    queryKey: ['sales-per-rep', tid, filters],
    enabled: !!tid,
    queryFn: () => fetchSalesPerRep(tid, filters),
    staleTime: 30 * 1000,
  })
  const rows = data?.rows ?? []
  const pagedRows = rows.slice((page - 1) * pageSize, page * pageSize)
  const hasStaff = staff.length > 0

  return (
    <div className="space-y-4">
      <PageHeader
        title="Sales per Rep"
        subtitle="Sales attributed to each representative"
        actions={
          <ExportButton
            filename={`sales-per-rep-${filters.from}-to-${filters.to}`}
            data={rows.map(r => ({ id: r.repId ?? 'unassigned' }))}
            fetchData={() => fetchSalesPerRepExport(tid, filters)}
            columns={[
              { key: 'representative', header: 'Representative' },
              { key: 'customers_assigned', header: 'Customers Assigned' },
              { key: 'sales_count', header: 'Sales Count' },
              { key: 'gross_sales', header: 'Gross Sales', isCurrency: true },
              { key: 'paid', header: 'Paid', isCurrency: true },
              { key: 'outstanding', header: 'Outstanding', isCurrency: true },
              { key: 'avg_sale_size', header: 'Avg Sale Size', isCurrency: true },
              { key: 'top_customer', header: 'Top Customer' },
            ]}
          />
        }
      />

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl border border-gray-100 bg-gray-50/60 px-4 py-2.5 text-sm">
        <span className="text-gray-500">Total Sales: <strong className="text-gray-900">{data?.totalSales ?? 0}</strong></span>
        <span className="hidden text-gray-300 sm:inline">|</span>
        <span className="text-gray-500">Total Reps: <strong className="text-gray-900">{data?.totalReps ?? 0}</strong></span>
      </div>

      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <DateRangePicker {...dr} tabs={[...PILL_TABS]} />
        <SearchInput
          value={search}
          onChange={e => setSearch(e.target.value)}
          onClear={() => setSearch('')}
          placeholder="Representative…"
          containerClassName="w-full sm:w-64"
        />
      </div>

      <p className="text-xs text-gray-400">All figures tied to sales in the selected period.</p>

      {isLoading ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : !hasStaff ? (
        <EmptyState
          icon={<Users className="h-8 w-8" />}
          title="No staff yet"
          description="Add staff in Payroll and assign them to customers to see rep attribution here."
        />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<BarChart3 className="h-8 w-8" />}
          title="No sales in this period"
          description="No sales were recorded in the selected period. Try widening the date range."
        />
      ) : (
        <div className="card overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[820px]">
              <thead>
                <tr className="border-b border-gray-100 text-left text-xs font-semibold uppercase tracking-wide text-gray-400">
                  <th className="px-4 py-3">Representative</th>
                  <th className="px-4 py-3 text-right">Customers</th>
                  <th className="px-4 py-3 text-right">Sales</th>
                  <th className="px-4 py-3 text-right">
                    <span className="inline-flex items-center gap-1" title="Grand total incl. tax, net of discounts">
                      Gross Sales <HelpCircle className="h-3 w-3 text-gray-300" />
                    </span>
                  </th>
                  <th className="px-4 py-3 text-right">Paid</th>
                  <th className="px-4 py-3 text-right">Outstanding</th>
                  <th className="px-4 py-3 text-right">Avg Sale</th>
                  <th className="px-4 py-3">Top Customer</th>
                </tr>
              </thead>
              <tbody>
                {pagedRows.map(r => {
                  const unassigned = r.repId === null
                  return (
                    <tr key={r.repId ?? 'unassigned'} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/60">
                      <td className="px-4 py-3 font-medium text-gray-900">
                        {unassigned ? (
                          <span className="italic text-gray-500">Unassigned</span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5">
                            {r.repName}
                            {r.repId && !staffIds.has(r.repId) && (
                              <span
                                className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500"
                                title="Non-staff representative (tracked only)"
                              >
                                non-staff
                              </span>
                            )}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {unassigned || r.customersAssigned == null ? (
                          <span className="text-gray-300">—</span>
                        ) : (
                          <Link to={`/contacts?tab=customers&representative_id=${r.repId}`} className="font-medium text-brand-600 hover:text-brand-700 hover:underline">
                            {r.customersAssigned}
                          </Link>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-700">{r.salesCount}</td>
                      <td className="px-4 py-3 text-right font-semibold text-gray-900 whitespace-nowrap">{format(r.gross)}</td>
                      <td className="px-4 py-3 text-right text-gray-600 whitespace-nowrap">{format(r.paid)}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <span className={r.outstanding > 0.005 ? 'font-medium text-red-600' : 'text-gray-400'}>{format(r.outstanding)}</span>
                      </td>
                      <td className="px-4 py-3 text-right text-gray-600 whitespace-nowrap">{format(r.avgSize)}</td>
                      <td className="px-4 py-3 text-gray-600">{r.topCustomerName ?? <span className="text-gray-300">—</span>}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <div className="px-4 pb-3 pt-1">
            <Pagination page={page} pageSize={pageSize} total={rows.length} onPageChange={setPage} onPageSizeChange={s => { setPageSize(s); setPage(1) }} />
          </div>
        </div>
      )}
    </div>
  )
}
